import {
  BoldOutlined,
  ItalicOutlined,
  LinkOutlined,
  LoadingOutlined,
  OrderedListOutlined,
  PictureOutlined,
  UnderlineOutlined,
  UnorderedListOutlined
} from "@ant-design/icons"
import ImageExtension from "@tiptap/extension-image"
import LinkExtension from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import UnderlineExtension from "@tiptap/extension-underline"
import { EditorContent, useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { Button, Popover, Select, Upload } from "antd"
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../utils/cn.ts"
import {
  type AttachmentChipAttrs,
  AttachmentChip,
  ListIndentKeymap,
  TOKEN_HIGHLIGHT_KEY,
  TokenHighlight
} from "./rich-text-extensions.ts"
import { toDisplayHtml } from "./rich-text-html.ts"

import "./rich-text-editor.css"
import { TextInput } from "../Input/TextInput.tsx"

interface RichTextEditorHandle {
  focus: () => void
  insertAttachmentChip: (attachment: AttachmentChipAttrs) => void
  insertText: (text: string) => void
}

interface ImageUploadResult {
  url: string
  alt?: string
}

interface RichTextEditorProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  autoFocus?: boolean
  disabled?: boolean
  minHeight?: number
  /** A fully-built control (e.g. an antd `Upload`-wrapped button) so the
   * caller's upload/validation stays out of this generic editor. */
  attachButton?: React.ReactNode
  ariaLabel?: string
  /** "compact" drops the Normal/Heading block-type select — a caller with no
   * heading in its allowed-tags list (e.g. the lead email composer) offering
   * it would produce markup its own backend then strips. */
  variant?: "full" | "compact"
  /** Turns on an inline "Insert Image" toolbar button. The upload itself
   * (validation, storage, the permanent URL) is entirely the caller's own —
   * this editor only inserts whatever `url`/`alt` the promise resolves with. */
  onImageUpload?: (file: File) => Promise<ImageUploadResult>
  /** Personalization fields (e.g. "[Customer Name]") to show in bold. */
  highlightTokens?: readonly string[]
}

const BLOCK_NORMAL = "normal"
const BLOCK_HEADING = "heading"

const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  function RichTextEditor(
    {
      value,
      onChange,
      placeholder,
      autoFocus,
      disabled,
      minHeight = 220,
      attachButton,
      ariaLabel,
      variant = "full",
      onImageUpload,
      highlightTokens
    },
    ref
  ) {
    const { t } = useTranslation()
    const [linkPopoverOpen, setLinkPopoverOpen] = useState(false)
    const [linkValue, setLinkValue] = useState("")
    const [uploadingImage, setUploadingImage] = useState(false)
    // Read by the highlight plugin on every redraw, so a list that arrives
    // after mount needs no new editor — only a nudge to redraw (below).
    const highlightTokensRef = useRef<readonly string[]>(highlightTokens ?? [])
    highlightTokensRef.current = highlightTokens ?? []

    const extensions = useMemo(
      () => [
        StarterKit.configure({
          heading: { levels: [2] },
          codeBlock: false,
          blockquote: false,
          horizontalRule: false,
          code: false,
          strike: false
        }),
        UnderlineExtension,
        LinkExtension.configure({
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
          // No `class` — the sanitizer strips it from saved HTML (see
          // rich-text-html.ts), so the look is keyed off `a[href]` in CSS.
          HTMLAttributes: {
            target: "_blank",
            rel: "noopener noreferrer nofollow"
          }
        }),
        Placeholder.configure({ placeholder: placeholder ?? "" }),
        AttachmentChip,
        ListIndentKeymap,
        TokenHighlight.configure({ getTokens: () => highlightTokensRef.current }),
        // Only registered when a caller actually offers an upload path — a
        // consumer with no image upload has nothing to sanitize an <img>
        // away from, but the node type would still accept a pasted one.
        ...(onImageUpload ? [ImageExtension.configure({ inline: false })] : [])
      ],
      // Changing `placeholder` after mount is not a supported live-update path
      // for this editor — every caller sets it once, from a stable i18n key.
      [!!onImageUpload]
    )

    const editor = useEditor({
      extensions,
      // `toDisplayHtml` is what makes this safe for a legacy plain-text
      // value (a note/template body saved before this editor's field
      // existed): a raw multi-`\n`-separated string handed to Tiptap as-is
      // parses as one run-on paragraph, since literal `\n` inside HTML text
      // content collapses to a single space rather than a break — real
      // editor output always opens with a recognizable tag, so anything
      // that doesn't is escaped and given real `<p>`/`<br>` structure first.
      // `value ?? ""`: antd Form.Item hands a controlled field `undefined`
      // when the form has no initial value for it (a brand-new record),
      // despite this prop's own `string` type saying otherwise.
      content: toDisplayHtml(value ?? ""),
      editable: !disabled,
      autofocus: autoFocus ? "end" : false,
      onUpdate: ({ editor: current }) => onChange(current.getHTML()),
      editorProps: {
        attributes: {
          role: "textbox",
          "aria-multiline": "true",
          ...(ariaLabel ? { "aria-label": ariaLabel } : {})
        }
      }
    })

    // `useEditor`'s `editable` option is only read at creation — without
    // this, the contentEditable region stayed live while a save was in
    // flight (WorkspaceCard sets `disabled` via `saving`), so a keystroke
    // during that window was never part of the already-captured payload.
    // `setEditable`'s second argument must be `false`: it defaults to `true`
    // and, undocumented, that means "emit an `update` event" — which fired
    // `onChange` on mount with nothing actually typed, making a legacy
    // plain-text note (re-serialized as `<p>...</p>` by the round trip)
    // register as dirty before the user touched anything.
    useEffect(() => {
      editor?.setEditable(!disabled, false)
    }, [editor, disabled])

    // `content` is read only once, at creation — a caller that overwrites
    // `value` from outside a keystroke (e.g. picking a saved template) would
    // otherwise see the field's label change with nothing to show for it.
    // Skipped on the mount render (that content already came in via `content`
    // above) and a no-op for the editor's own keystrokes, since `onChange`
    // already set `value` to this exact `getHTML()`. Routed through
    // `toDisplayHtml` for the same reason the initial `content` above is:
    // an externally-set value can itself be legacy plain text (e.g. an old
    // personal template with no HTML), and the helper is a no-op for a value
    // that already looks like real editor output.
    const hasMounted = useRef(false)
    useEffect(() => {
      if (!hasMounted.current) {
        hasMounted.current = true
        return
      }
      if (editor && value !== editor.getHTML()) {
        editor.commands.setContent(toDisplayHtml(value ?? ""), false)
      }
    }, [editor, value])

    const highlightSignature = (highlightTokens ?? []).join("|")
    useEffect(() => {
      if (!editor || editor.isDestroyed) return
      editor.view.dispatch(editor.state.tr.setMeta(TOKEN_HIGHLIGHT_KEY, highlightSignature))
    }, [editor, highlightSignature])

    useImperativeHandle(
      ref,
      () => ({
        focus: () => editor?.chain().focus().run(),
        // An upload resolves asynchronously — the user is often still typing
        // elsewhere by the time it does, so this appends at the end without
        // `updateSelection`, which would otherwise yank their cursor away
        // from wherever they currently are.
        insertAttachmentChip: (attachment) =>
          editor
            ?.chain()
            .insertContentAt(
              editor.state.doc.content.size,
              [
                {
                  type: "paragraph",
                  content: [{ type: "attachmentChip", attrs: attachment }]
                }
              ],
              { updateSelection: false }
            )
            .run(),
        // Unlike an attachment, this is a direct user action (e.g. clicking a
        // merge-token chip), so it lands at the cursor and keeps focus there.
        insertText: (text) => {
          editor?.chain().focus().insertContent(text).run()
        }
      }),
      [editor]
    )

    if (!editor) return null

    const blockValue = editor.isActive("heading", { level: 2 })
      ? BLOCK_HEADING
      : BLOCK_NORMAL

    const setBlockValue = (next: string) => {
      const chain = editor.chain().focus()
      if (next === BLOCK_HEADING) chain.setHeading({ level: 2 }).run()
      else chain.setParagraph().run()
    }

    const openLinkPopover = (open: boolean) => {
      if (open) setLinkValue(editor.getAttributes("link").href ?? "")
      setLinkPopoverOpen(open)
    }

    // The caller's own promise owns telling the user why an upload failed
    // (a toast, typically) — this only has to not let that rejection escape
    // as an unhandled one, and to always clear the loading state either way.
    const handleImageFile = async (file: File) => {
      if (!onImageUpload) return
      setUploadingImage(true)
      try {
        const { url, alt } = await onImageUpload(file)
        editor
          .chain()
          .focus()
          .setImage({ src: url, alt: alt ?? file.name.replace(/\.[^.]+$/, "") })
          .run()
      } catch {
        // Reported to the user by the caller's own onImageUpload rejection.
      } finally {
        setUploadingImage(false)
      }
    }

    const applyLink = () => {
      const url = linkValue.trim()
      if (!url) {
        editor.chain().focus().extendMarkRange("link").unsetLink().run()
      } else if (editor.state.selection.empty) {
        editor
          .chain()
          .focus()
          .insertContent({
            type: "text",
            text: url,
            marks: [{ type: "link", attrs: { href: url } }]
          })
          .run()
      } else {
        editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run()
      }
      setLinkPopoverOpen(false)
    }

    const toolbarButton = (
      active: boolean,
      onClick: () => void,
      icon: React.ReactNode,
      label: string
    ) => (
      <Button
        type={"text"}
        size={"small"}
        aria-label={label}
        aria-pressed={active}
        disabled={disabled}
        className={cn(active && "bg-primary-bg text-primary-strong")}
        // A plain button naturally steals DOM focus/selection from the
        // contentEditable on click; preventing mousedown's default keeps
        // the editor's selection intact so a toggle over selected text
        // applies to that selection rather than a collapsed cursor.
        onMouseDown={(event) => event.preventDefault()}
        onClick={onClick}
      >
        {icon}
      </Button>
    )

    return (
      <div
        className={cn(
          "rich-text-editor rounded-md border border-border overflow-hidden",
          disabled && "opacity-70"
        )}
      >
        <div
          className={
            "flex flex-wrap items-center gap-0.5 px-2 py-1 border-b border-border bg-muted"
          }
        >
          {variant === "full" ? (
            <>
              <Select
                size={"small"}
                variant={"borderless"}
                value={blockValue}
                disabled={disabled}
                aria-label={t("richTextEditor.toolbar.textStyle")}
                className={"w-[92px]"}
                popupMatchSelectWidth={false}
                options={[
                  { value: BLOCK_NORMAL, label: t("richTextEditor.toolbar.normal") },
                  { value: BLOCK_HEADING, label: t("richTextEditor.toolbar.heading") }
                ]}
                onChange={setBlockValue}
              />
              <span className={"w-px h-4 bg-border mx-1"} />
            </>
          ) : null}
          {toolbarButton(
            editor.isActive("bold"),
            () => editor.chain().focus().toggleBold().run(),
            <BoldOutlined />,
            t("richTextEditor.toolbar.bold")
          )}
          {toolbarButton(
            editor.isActive("italic"),
            () => editor.chain().focus().toggleItalic().run(),
            <ItalicOutlined />,
            t("richTextEditor.toolbar.italic")
          )}
          {toolbarButton(
            editor.isActive("underline"),
            () => editor.chain().focus().toggleUnderline().run(),
            <UnderlineOutlined />,
            t("richTextEditor.toolbar.underline")
          )}
          <span className={"w-px h-4 bg-border mx-1"} />
          {toolbarButton(
            editor.isActive("bulletList"),
            () => editor.chain().focus().toggleBulletList().run(),
            <UnorderedListOutlined />,
            t("richTextEditor.toolbar.bulletList")
          )}
          {toolbarButton(
            editor.isActive("orderedList"),
            () => editor.chain().focus().toggleOrderedList().run(),
            <OrderedListOutlined />,
            t("richTextEditor.toolbar.orderedList")
          )}
          <span className={"w-px h-4 bg-border mx-1"} />
          <Popover
            trigger={"click"}
            open={linkPopoverOpen}
            onOpenChange={openLinkPopover}
            placement={"bottomLeft"}
            content={
              <div className={"flex items-center gap-2 w-64"}>
                <TextInput
                  disableAutoCapitalize
                  size={"small"}
                  autoFocus
                  placeholder={t("richTextEditor.link.placeholder")}
                  value={linkValue}
                  onChange={(event) => setLinkValue(event.target.value)}
                  onPressEnter={applyLink}
                />
                <Button size={"small"} type={"primary"} onClick={applyLink}>
                  {t("richTextEditor.link.apply")}
                </Button>
              </div>
            }
          >
            {toolbarButton(
              editor.isActive("link"),
              () => undefined,
              <LinkOutlined />,
              t("richTextEditor.toolbar.link")
            )}
          </Popover>
          {onImageUpload ? (
            <Upload
              accept={"image/*"}
              showUploadList={false}
              disabled={disabled || uploadingImage}
              beforeUpload={(file) => {
                void handleImageFile(file)
                return false
              }}
            >
              {toolbarButton(
                false,
                () => undefined,
                uploadingImage ? <LoadingOutlined /> : <PictureOutlined />,
                t("richTextEditor.toolbar.image")
              )}
            </Upload>
          ) : null}
          {attachButton}
        </div>

        <EditorContent
          editor={editor}
          className={"rich-text-content px-3 py-2 overflow-y-auto tailwind-scroll"}
          style={{ minHeight }}
        />
      </div>
    )
  }
)

export { type RichTextEditorHandle, type RichTextEditorProps, RichTextEditor }
